import { memo } from "react";
import { Check } from "lucide-react";
import {
  ROW_BORDER,
  ROW_SHAPE,
  ROW_SURFACE,
  ROW_SURFACE_SELECTED,
} from "../layout";

interface OptionRowProps {
  label: string;
  /** Supporting line under the label (the `rich` density). */
  blurb?: string;
  /** Small accent line under the label — "Coming soon" and the like. */
  badge?: string;
  /** Square thumbnail on the left (the `rich` density with imagery). */
  photoSrc?: string;
  emoji?: string;
  selected: boolean;
  onClick: () => void;
  /** Stagger index — options enter with a 40ms cascade, per the design. */
  index?: number;
  /** `list` is the compact row; `rich` gives it more air and a blurb. */
  density?: "list" | "rich";
}

/**
 * One selectable option. Used by every list-style question in the flow, so
 * selection state looks and animates identically everywhere.
 *
 * Selected state is a tinted surface + brand border + a filled check on the
 * right; unselected is a hairline glass row. Both work on light and dark.
 *
 * Selecting must never change the row's geometry. Three things would, and all
 * three are pinned here rather than toggled:
 *   - border width (always 1.5px, only the color changes)
 *   - font weight (constant — bolding re-measures the text and can rewrap it)
 *   - the check mark (its slot is always reserved, only its opacity changes)
 * Without this a two-line label reflows to three the moment you tap it, and
 * every row below jumps.
 */
function OptionRowBase({
  label,
  blurb,
  photoSrc,
  emoji,
  badge,
  selected,
  onClick,
  index = 0,
  density = "list",
}: OptionRowProps) {
  const rich = density === "rich";

  return (
    <button
      type="button"
      onClick={onClick}
      // The cascade, and the press. Both used to be framer props; both are
      // now the browser's own, because this row is the most-tapped element in
      // the flow and every tap re-renders the screen around it.
      style={{ animationDelay: `${Math.min(index * 0.04, 0.32)}s` }}
      aria-pressed={selected}
      className={`onboarding-row-in w-full text-left flex items-center gap-3.5 transition-colors active:scale-[0.99] ${ROW_BORDER} ${
        // The rich density trades side padding for the thumbnail; everything
        // else shares one shape so lists line up across screens.
        rich ? "rounded-2xl p-3" : ROW_SHAPE
      } ${selected ? ROW_SURFACE_SELECTED : ROW_SURFACE}`}
    >
      {photoSrc && (
        <img
          src={photoSrc}
          alt=""
          aria-hidden
          className="w-14 h-14 rounded-2xl object-cover flex-none"
          style={{ filter: "sepia(.28) saturate(.82)" }}
        />
      )}
      {!photoSrc && emoji && (
        <span className="text-xl flex-none leading-none">{emoji}</span>
      )}

      <span className="flex-1 min-w-0">
        <span
          className="block font-medium text-[15px] leading-snug text-neutral-900 dark:text-neutral-50"
        >
          {label}
        </span>
        {blurb && (
          <span className="block text-[12px] leading-snug text-neutral-500 dark:text-neutral-400 mt-1">
            {blurb}
          </span>
        )}
        {badge && (
          <span className="block text-[11px] tracking-[0.14em] uppercase font-semibold text-primary-400 mt-1.5">
            {badge}
          </span>
        )}
      </span>

      {/* Always in the layout — the transition only scales it into view. The
          curve overshoots on its way to 1, which is the part of the old spring
          worth keeping; what isn't is a physics solver running per frame on
          the element that answers the tap. */}
      <span
        aria-hidden
        style={{
          transform: `scale(${selected ? 1 : 0.4})`,
          opacity: selected ? 1 : 0,
        }}
        className="w-5 h-5 flex-none rounded-full bg-primary-500 flex items-center justify-center transition-[transform,opacity] duration-200 ease-[cubic-bezier(.2,1.5,.4,1)] motion-reduce:transition-none"
      >
        {/* Knocked out in the canvas colour, so it reads as a hole punched in
            the dot rather than a second bright element beside the label — the
            canvas just isn't the same colour in both themes. On dark the tick
            is the near-black ground (6.0:1 on the orange dot); on light it is
            the white one (3.26:1, still clear of the 3:1 an icon needs). */}
        <Check
          className="w-3.5 h-3.5 text-white dark:text-[#0C0B10]"
          strokeWidth={3}
        />
      </span>
    </button>
  );
}

/**
 * Memoised, because a multi-select re-renders its whole screen on every tap.
 *
 * The answer lives at the top of the flow, so one tap re-renders the page, the
 * runner, the shell and the screen — and with it every row. Only the row that
 * changed has any work to do, so only it re-renders now: the other four are
 * the same output for the same props. This mattered more when the row was two
 * framer components re-resolving props and variants on every render; it is
 * cheap either way, and it stays because the saving is real regardless.
 *
 * `onClick` is the one prop that must stay stable for this to hold — the
 * screens build it with `useCallback`.
 */
const OptionRow = memo(OptionRowBase);

export default OptionRow;
